import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '../../store/app'
import type { PendingInvite } from '../../store/types'
import { keychain } from '../../api/keychain'
import {
  acceptInvites,
  blockOrgInvites,
  declineInvite,
  fetchMyInvites,
  reconnectWS,
  requestMyInvites,
} from '../../api/ws'
import './InvitationsSheet.css'

/// The invitee's view of households that have asked them to join.
///
/// There is no token anywhere in this screen. The list is resolved
/// server-side from the signed-in user's verified email, so the only
/// thing the user ever does is choose.
///
/// The list is loaded ONCE and then owned locally — see the snapshot note
/// below. Rows leave only because the user declined or blocked them.
///
/// `offerMode` is the post-signup variant: the user has no household yet,
/// so declining everything has to leave them somewhere. It surfaces a
/// "create my own instead" escape and is the only place this sheet is
/// shown unprompted.
export function InvitationsSheet({
  offerMode = false,
  onCreateOwn,
}: {
  offerMode?: boolean
  onCreateOwn?: () => void
}) {
  const ioDeviceID = useAppStore((s) => s.ioDeviceID) ?? ''
  const connectionStatus = useAppStore((s) => s.connectionStatus)
  const popSheet = useAppStore((s) => s.popSheet)
  const pushToast = useAppStore((s) => s.pushToast)

  // This list is a SNAPSHOT, not a subscription. The store's myInvites
  // keeps updating live for badges elsewhere, but a multi-select whose
  // rows can appear and vanish underneath it is a screen where "Join"
  // means something different between reading and tapping. So the sheet
  // loads once, owns the result, and edits it itself as the user acts.
  //
  // Seeded from the store because enrollment puts invitations there
  // before a WebSocket exists — a brand-new invitee sees the offer on
  // their first frame rather than a spinner. getState() rather than the
  // hook: read once, don't subscribe.
  const [invites, setInvites] = useState<PendingInvite[]>(
    () => useAppStore.getState().myInvites,
  )
  // Pre-checked. Someone opening this screen is almost always here to say
  // yes, and unchecking is cheaper than hunting for a checkbox. Joining
  // several is harmless — the only real decision is which one you land
  // in, and that's named before committing.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(useAppStore.getState().myInvites.map((i) => i.id)),
  )
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  // Latches on a successful join. Guards the button against a second
  // tap during the gap between accepting and the app re-routing — that
  // gap is real (it takes a server push), and a second tap hits a
  // consumed invitation and reports it as an error, which is how this
  // looked like a failure when the accept had in fact worked.
  const [joined, setJoined] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmBlock, setConfirmBlock] = useState<PendingInvite | null>(null)

  // The one load. Fires once the socket is up; after this the list only
  // changes in response to something the user did.
  const loaded = useRef(false)
  useEffect(() => {
    if (loaded.current || connectionStatus !== 'connected') return
    loaded.current = true
    requestMyInvites()
      .then((res) => {
        const list = res.invites ?? []
        setInvites(list)
        setSelected(new Set(list.map((i) => i.id)))
      })
      .catch(() => setError('Could not load invitations. Pull down to retry.'))
      .finally(() => setLoading(false))
  }, [connectionStatus])

  // Remove rows the user has dealt with, keeping the selection in step.
  // Takes explicit ids rather than a predicate so neither updater has to
  // close over `invites` — a stale read there would resurrect a row.
  function dropInviteIDs(ids: string[]) {
    const gone = new Set(ids)
    setInvites((prev) => prev.filter((i) => !gone.has(i.id)))
    setSelected((prev) => {
      const next = new Set<string>()
      for (const id of prev) if (!gone.has(id)) next.add(id)
      return next
    })
    // Keep the live store (and any badge reading it) honest.
    fetchMyInvites()
  }

  // The server lands the device in the household whose invitation has
  // been waiting longest, and the list is oldest-first. Mirror that here
  // so the button can name it before the user commits.
  const landing = useMemo(() => {
    const chosen = invites.filter((i) => selected.has(i.id))
    return chosen.length > 0 ? chosen[0] : null
  }, [invites, selected])

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // In offerMode this sheet is rendered inline by OrphanedScreen, not in
  // the sheet stack — popSheet() would pop nothing (or something
  // unrelated). That screen unmounts on its own once the membership lands
  // and organizations_list arrives.
  function dismiss() {
    if (!offerMode) popSheet()
  }

  async function join() {
    if (selected.size === 0 || busy || joined) return
    setBusy(true)
    setError(null)
    try {
      const res = await acceptInvites({ inviteIDs: [...selected], ioDeviceID })
      if (res.error) {
        setError(res.error)
        return
      }
      const accepted = res.accepted ?? []
      const failedCount = Object.keys(res.failed ?? {}).length
      if (accepted.length === 0) {
        // Nothing was joined. Overwhelmingly this means the invitations
        // were already accepted — a second tap before the app re-routed.
        // Re-read the truth instead of asserting they're "no longer
        // available", which is alarming and, after a successful join,
        // simply wrong.
        const fresh = await requestMyInvites().catch(() => null)
        const list = fresh?.invites ?? []
        setInvites(list)
        setSelected(new Set(list.map((i) => i.id)))
        if (list.length === 0) {
          setJoined(true)
          reconnectWS()
          dismiss()
        } else {
          setError('Those invitations are no longer available.')
        }
        return
      }
      // The server already switched the device's current org during
      // accept. Persist it to the keychain so a future boot reads the
      // right household directly instead of "" (the value an invitee
      // enrolled with). Best-effort; the store and server already agree.
      if (res.landing_organization_id) {
        void keychain.setOrganizationID(res.landing_organization_id).catch(() => {})
      }
      const landedName =
        accepted.find((a) => a.organization_id === res.landing_organization_id)
          ?.organization_name ?? accepted[0].organization_name
      pushToast(
        accepted.length === 1
          ? `Joined ${landedName}`
          : `Joined ${accepted.length} households — you're in ${landedName}`,
      )
      if (failedCount > 0) {
        pushToast(`${failedCount} invitation${failedCount > 1 ? 's' : ''} could not be accepted`)
      }
      setJoined(true)
      // Reconnect so the server replays its full connect-time push for the
      // now-current household — orgs (which re-routes the app out of the
      // offer/orphaned state), agents, positions, rules. This is the same
      // path a manual refresh takes, and the one place we KNOW delivers
      // the new household's scoped lists.
      reconnectWS()
      dismiss()
    } catch {
      setError('Could not join. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  async function decline(invite: PendingInvite) {
    setBusy(true)
    setError(null)
    try {
      const res = await declineInvite({ inviteID: invite.id })
      if (res.error) setError(res.error)
      else dropInviteIDs([invite.id])
    } catch {
      setError('Could not decline. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function block(invite: PendingInvite) {
    setBusy(true)
    setError(null)
    setConfirmBlock(null)
    try {
      const res = await blockOrgInvites({
        organizationID: invite.organization_id,
        blocked: true,
      })
      if (res.error) setError(res.error)
      else {
        pushToast(`Blocked invitations from ${invite.organization_name}`)
        // Blocking clears every invitation from that household, not just
        // the row that was tapped.
        dropInviteIDs(
          invites
            .filter((i) => i.organization_id === invite.organization_id)
            .map((i) => i.id),
        )
      }
    } catch {
      setError('Could not block. Try again.')
    } finally {
      setBusy(false)
    }
  }

  // offerMode has no sheet to dismiss, so say something rather than
  // leaving the just-tapped list on screen while routing catches up.
  if (joined) {
    return (
      <div className="invitations">
        <p className="invitations__empty">Joined. Taking you there…</p>
      </div>
    )
  }

  if (loading && invites.length === 0) {
    return (
      <div className="invitations">
        <p className="invitations__empty">Loading…</p>
      </div>
    )
  }

  if (invites.length === 0) {
    return (
      <div className="invitations">
        <p className="invitations__empty">
          {offerMode
            ? 'No invitations are waiting for you.'
            : "You don't have any invitations right now."}
        </p>
        {offerMode && onCreateOwn && (
          <button className="invitations__secondary" onClick={onCreateOwn}>
            Create my own household
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="invitations">
      <p className="invitations__intro">
        {offerMode
          ? invites.length === 1
            ? 'A household has invited you to join.'
            : 'These households have invited you to join.'
          : 'Choose which households to join. You can belong to more than one.'}
      </p>

      <ul className="invitations__list">
        {invites.map((invite) => {
          const isSelected = selected.has(invite.id)
          return (
            <li key={invite.id} className="invitations__item">
              <label className="invitations__row">
                <input
                  type="checkbox"
                  className="invitations__check"
                  checked={isSelected}
                  disabled={busy}
                  onChange={() => toggle(invite.id)}
                />
                <span className="invitations__text">
                  {/* Both strings are chosen by a stranger. React escapes
                      them; the server has already stripped control and
                      bidi characters and capped the length. Never render
                      either as markup. */}
                  <span className="invitations__org">{invite.organization_name}</span>
                  <span className="invitations__meta">
                    Invited by {invite.inviter_email}
                  </span>
                  <span className="invitations__meta">
                    Expires {formatExpiry(invite.expires_at)}
                  </span>
                </span>
              </label>
              <div className="invitations__actions">
                <button
                  className="invitations__link"
                  disabled={busy}
                  onClick={() => void decline(invite)}
                >
                  Decline
                </button>
                <button
                  className="invitations__link invitations__link--danger"
                  disabled={busy}
                  onClick={() => setConfirmBlock(invite)}
                >
                  Block
                </button>
              </div>
            </li>
          )
        })}
      </ul>

      {error && <p className="invitations__error">{error}</p>}

      <button
        className="invitations__primary"
        disabled={selected.size === 0 || busy || joined}
        onClick={() => void join()}
      >
        {selected.size <= 1
          ? 'Join'
          : `Join ${selected.size} households`}
      </button>
      {selected.size > 1 && landing && (
        <p className="invitations__hint">
          You&apos;ll start in {landing.organization_name}. Switch anytime from
          Household.
        </p>
      )}

      {offerMode && onCreateOwn && (
        <button className="invitations__secondary" disabled={busy} onClick={onCreateOwn}>
          Create my own household instead
        </button>
      )}

      {confirmBlock && (
        <div className="invitations__confirm">
          <p className="invitations__confirm-body">
            Block invitations from{' '}
            <strong>{confirmBlock.organization_name}</strong>? You won&apos;t
            see invitations from this household again. They won&apos;t be told.
          </p>
          <button
            className="invitations__primary invitations__primary--danger"
            onClick={() => void block(confirmBlock)}
          >
            Block
          </button>
          <button
            className="invitations__secondary"
            onClick={() => setConfirmBlock(null)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}

// Coarse on purpose — "in 6 days" is what matters, not the timestamp.
function formatExpiry(iso: string): string {
  const t = Date.parse(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'))
  if (Number.isNaN(t)) return 'soon'
  const days = Math.round((t - Date.now()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'tomorrow'
  return `in ${days} days`
}
